// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import Switch from 'react-switch';

import Select2, { DataSelect2 } from 'wdna-select2';
import DatePicker from 'wdna-date-selector';

import { UserDO } from 'wdna-components';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { ContextType, FilterContext } from '../../../../../types';
import { typeSensorVariable, TypeSensorVariableListDO, typeSensor, TypeSensorListDO } from '../../../../configuration/requests';
import { company, CompanyDO } from '../../../../management/requests';
import { sensor, SensorDO, SensorListDO } from '../../../requests';

interface Props {
  rowId?: number;
}

interface State {
  companyArray: CompanyDO[];
  typeSensorArray: TypeSensorListDO[];
  sensorArray: SensorListDO[];

  date: Date[];
  flatpickrMode: string;
  groupDateTime: string;

  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;

  checked: boolean;
  autoRefresh: boolean;

  id?: number;
  companyId?: number;
  typeSensorId?: number;
}

export default class Filter extends React.PureComponent<Props, State> {
  private pid: any;
  private mounted: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    /* INITIAL FILTER STATE */
    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 1);
    const dEnd: Date = new Date();

    this.state = {
      companyArray: [],
      typeSensorArray: [],
      sensorArray: [],

      date: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',

      checked: false,
      autoRefresh: false,
    };
  }

  /* React Lifecycle methods */
  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    if (this.props.rowId) return;

    this.loadingStart();
    try {
      const [typeSensorArray, companyArray] = await Promise.all([
        typeSensor<TypeSensorListDO[]>('list', undefined, { active: true }, { params: { order: 'typeModule' } }) || [],
        company<CompanyDO[]>('list') || [],
      ]);
      if (this.mounted) {
        this.setState({
          typeSensorArray,
          companyArray,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public isToday = (): boolean => {
    let a: boolean = true;
    const b: string = moment(this.state.date[1]).format('YYYY-MM-DD');
    const c: string = moment(new Date()).format('YYYY-MM-DD');
    if (b !== c) {
      a = false;
    }

    return a;
  };

  public loadVariable = async (): Promise<void> => {
    const idSensor: number | undefined = this.state.id || this.props.rowId;
    if (idSensor !== undefined) {
      const sensorInfo: SensorDO | undefined = await sensor<SensorDO>('get', String(idSensor));
      const typeSensorId: number | undefined = sensorInfo?.typeSensor?.id;

      const typeVariableArray: TypeSensorVariableListDO[] = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId }) || [];
      const dataVariables: any[] = typeVariableArray.map((row: TypeSensorVariableListDO) => {
        // TODO: review ???
        const newRow: any = `${row.variableName} ${row.variableUnit}`;
        delete newRow.values;
        return newRow;
      });

      this.context?.updateParent({
        data3: dataVariables,
      });
    }
  };

  public hideSidebar = (): void => {
    $('#right-panel').addClass('hidden-functions');
    $('#tab-main-content').removeClass('tab-left-content');
    $('#toggle-func-btn').addClass('transparent sidebar-hidden');
  };

  public submit = async (): Promise<void> => {
    await this.loadVariable();
    const idSensor: number | undefined = this.state.id || this.props.rowId;
    if (!idSensor) {
      messageError('Error', 'Please select a device');
      return;
    }

    const req: Record<string, any> = {};
    if (this.state.dateIni) {
      req.from = this.state.dateIni;
      if (this.state.timeIni) req.from = `${req.from}T${this.state.timeIni}:00.000Z`;
    }
    if (this.state.dateEnd) {
      req.to = this.state.dateEnd;
      if (this.state.timeEnd) req.to = `${req.to}T${this.state.timeEnd}:00.000Z`;
    }

    this.loadingStart();
    try {
      const data: any[] = await sensor('register_list', String(idSensor), req) || [];
      if (this.mounted) {
        this.context?.updateParent({
          data,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
    this.hideSidebar();
  };

  public updateMainFilter = (filter: any): void => {
    const newFilter: any = _.cloneDeep(filter);

    this.context?.updateParent({
      ...newFilter,
    });
  };

  public loadingStart = (): void => {
    this.context?.updateParent({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.context?.updateParent({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  /* Handlers */
  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    const { companyId, typeSensorId } = this.state;

    const request: Record<string, any> = {};
    if (companyId) request.companyId = companyId;
    if (typeSensorId) request.typeSensorId = typeSensorId;
    request[name] = value;

    this.loadingStart();
    sensor<SensorListDO[]>('list', undefined, request)
      ?.then((resp: SensorListDO[]) => {
        this.setState((prevState: State) => ({
          ...prevState,
          [name]: +value,
          id: undefined,
          sensorArray: resp,
        }));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChangeDevice = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public handleDatePickerChanges = (config: any): void => {
    const date: Date[] = config.getDates();
    this.setState({
      date,
      flatpickrMode: config.getMode(),
    });
    const preDataIni: Date = date[0];
    const newDataIni: string = moment(preDataIni).format('YYYY-MM-DD');
    this.setState({ dateIni: newDataIni });
    const preDataEnd: Date = date[1];
    if (this.state.flatpickrMode === 'range') {
      const newDataEnd: string = moment(preDataEnd).format('YYYY-MM-DD');
      this.setState({ dateEnd: newDataEnd });
    } else {
      const copyData: string = moment(newDataIni).add(1, 'days').format('YYYY-MM-DD');
      this.setState({ dateEnd: copyData });
    }
  };

  public handleChecked = (checked: boolean): void => {
    this.setState({
      checked,
    });
  };

  public onAutoRefresh = (): boolean => {
    if (this.state.checked === true && this.state.autoRefresh === false) {
      this.setState({
        autoRefresh: true,
      });
      setTimeout(() => {
        if (this.state.checked === true && this.state.autoRefresh === true) {
          this.submit().catch(() => { /* pass */ });
        }
        this.setState({
          autoRefresh: false,
        });
      }, 60000);
    }

    return this.state.checked;
  };

  public render(): JSX.Element {
    const { companyArray, typeSensorArray, sensorArray } = this.state;
    const { date, flatpickrMode, groupDateTime } = this.state;

    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    const userIsAdmin: boolean = (user.type === 'adm');
    const showMainFilter: boolean = (this.props.rowId == null);

    return (
      <div className="main-filters">
        <div hidden={this.isToday() === false}>
          <div className="auto-refresh-button">
            <br />
            <label htmlFor="normal-switch" style={{ marginRight: '20px' }}>
              <Switch
                id="auto-refresh-button"
                className="react-switch"
                aria-labelledby="neat-label"
                height={30}
                onColor="#78a329"
                offColor="#5c6875"
                offHandleColor="#444"
                onHandleColor="#b7f93d"
                checked={this.onAutoRefresh()}
                onChange={this.handleChecked}
              />
              <span style={{ marginLeft: '10px' }}>Auto Refresh</span>
            </label>
          </div>
        </div>
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="companyId"
              data={(companyArray || [])
                .filter((item: CompanyDO) => {
                  return userIsAdmin || item.name === user?.extra?.company;
                })
                .map((item: CompanyDO): DataSelect2 => {
                  return {
                    id: item.id,
                    text: item.name,
                    title: item.name,
                  };
                })}
              value={this.state.companyId}
              options={{
                placeholder: 'Select Company',
              }}
              onChange={this.handleChangeFilter}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="typeSensorId"
              data={(typeSensorArray || []).map((item: TypeSensorListDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.name}`,
                  title: `${item.name} [${item.code}]`,
                };
              })}
              value={this.state.typeSensorId}
              options={{
                placeholder: 'Select Sensor Type',
              }}
              onChange={this.handleChangeFilter}
              style={{ width: '100%' }}
            />
          </div>
        )}
        {showMainFilter && (
          <div className="sideFilter" style={{ marginBottom: '15px' }}>
            <Select2
              name="id"
              data={(sensorArray || []).map((item: SensorListDO): DataSelect2 => {
                return {
                  id: item.id,
                  text: `${item.description}`,
                  title: `${item.code} - ${item.name}`,
                };
              })}
              value={this.state.id}
              options={{
                placeholder: 'Select Sensor To Show',
              }}
              onChange={this.handleChangeDevice}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <DatePicker
          icon
          hours={[]}
          dates={date}
          showChangeModes
          mode={flatpickrMode}
          granularity={groupDateTime}
          iconClass="wdna-ico-icon-calendar"
          handleChanges={this.handleDatePickerChanges}
        />
        <div style={{ marginTop: '1px' }}>
          <button type="button" className="filter-btn btn btn-default" onClick={(): void => { this.submit().catch(() => { /* pass */ }); }}>
            <i className="wdna-ico-icon-playsearch" />
            <span>GO</span>
          </button>
        </div>
      </div>
    );
  }
}
